import { z } from 'zod';
import { isoDateStringSchema, objectIdSchema } from './common.js';
import { sketchSceneSchema } from './sketch-scene.js';

/**
 * The drawing that goes with a set of measurements — the silhouette, the arrows, "left shoulder
 * drops" in the tailor's own hand. Numbers say how big; a sketch says what shape.
 *
 * A sketch is a ROW OF ITS OWN that is never edited. Every save of the canvas creates a new one,
 * and whatever owns a drawing — a customer's measurement profile, a garment type's starting
 * outline, one order item's frozen copy — holds a {@link MeasurementSketchRef} to it. That is the
 * whole trick: an order item that froze a profile's sketch at booking keeps pointing at the same
 * row when the profile is redrawn next month, because the redraw is a different row. Nothing is
 * copied and nothing can be rewritten underneath an order.
 *
 * A saved sketch is two things, exactly as on the design board: the SCENE (the editable drawing,
 * opaque JSON, fetched only when somebody opens the canvas) and a PNG rendering pushed through the
 * ordinary media pipeline, which is what every list, card and karigar screen shows.
 */

/** The `entityType` a sketch's PNG rendering is filed under in the media pipeline. */
export const MEASUREMENT_SKETCH_MEDIA_ENTITY_TYPE = 'measurement_sketch';

/**
 * A pointer to a sketch, with the picture to show for it. The preview id is copied beside the
 * pointer because a sketch row never changes, so the copy can never go stale — and so a list of
 * profiles or an order's items render their drawings without a join.
 */
export const measurementSketchRefSchema = z.object({
  sketchId: objectIdSchema,
  /** Null when the drawing saved but its picture could not be exported or uploaded. */
  previewMediaId: objectIdSchema.nullable(),
});
export type MeasurementSketchRef = z.infer<typeof measurementSketchRefSchema>;

/**
 * Saving a drawing: always a create. `previewMediaId` is the PNG the client has already uploaded
 * (unattached); the server attaches it to the new sketch. Omitted when the export failed — the
 * drawing is what cannot be regenerated, so it saves without its picture rather than not at all.
 */
export const createMeasurementSketchSchema = z.object({
  garmentTypeKey: z.string().min(1),
  scene: sketchSceneSchema,
  previewMediaId: objectIdSchema.nullish(),
});
export type CreateMeasurementSketchInput = z.input<typeof createMeasurementSketchSchema>;

export const measurementSketchSchema = z.object({
  id: objectIdSchema,
  garmentTypeKey: z.string(),
  scene: z.string(),
  previewMediaId: objectIdSchema.nullable(),
  createdByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type MeasurementSketch = z.infer<typeof measurementSketchSchema>;
